import { type PropsWithChildren, useEffect, useRef, useState } from "react";
import type { AriaMenuProps } from "react-aria";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Item, MenuListBox, MenuOptionItem, Popover, TriggerButton, useContextualMenu } from "widgets";

import {
  getIconElementForPlaybook,
  isPlaybookExecutableInGenomeViewerContext,
  loadPlaybooks,
} from "@/app-utils/playbook.ts";
import { updateNfPlaybookExecutionState } from "@/redux/modules/nf-playbook-execution.ts";
import { ProtectedResourceLogo } from "@/ui-modules/core/ProtectedResourceLogo.tsx";

import { type Configuration, type InputTableQuerySpec, type Playbook } from "../../graphql.js";
import { type IGenomeBrowserState, type IMutantGenome } from "../../redux/modules/genome-browser.js";
import { openPlaybook } from "../../redux/modules/playbook-execution.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import {
  DATATABLE_ID_TYPE,
  ENTITY_TYPE,
  PLAYBOOK_TYPE,
  TRACK_TYPE,
  UNKNOWN_GENOME_ID,
} from "../../settings/settings.js";
import menuStyle from "./styles/ContextualMenu.module.scss";

const RangeAnalysisPlaybooksButton = () => {
  const triggerRef = useRef(null);
  const { state, triggerProps, menuProps } = useContextualMenu(triggerRef);

  return (
    <>
      <TriggerButton
        {...triggerProps}
        ref={triggerRef}
        icon={"desktop_windows"}
        label={"Playbooks"}
        tooltip={"Run a Playbook that uses the current range selection for the analysis"}
        id={"button-header-playbooks"}
        showLabel
      />
      <Popover state={state} triggerRef={triggerRef} placement={"bottom start"}>
        <RangeAnalysisPlaybooksMenuDialog {...(menuProps as PropsWithChildren<AriaMenuProps<object>>)} />
      </Popover>
    </>
  );
};

const RangeAnalysisPlaybooksMenuDialog = (props: AriaMenuProps<object>) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration, shallowEqual);
  const genomeBrowser = useSelector<IAppState, IGenomeBrowserState>((state) => state.genomeBrowser, shallowEqual);

  const [isLoading, setLoading] = useState<boolean>(true);
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);

  useEffect(() => {
    void (async () => {
      const playbooks = await dispatch(loadPlaybooks());

      const filteredPlaybooks = playbooks.filter(
        (p) =>
          isPlaybookExecutableInGenomeViewerContext(p) &&
          ((p.type?.id === PLAYBOOK_TYPE.RANGE_ANALYSIS && genomeBrowser?.contig?.organism.id !== UNKNOWN_GENOME_ID) ||
            (p.type?.id === PLAYBOOK_TYPE.ALIGNMENT &&
              genomeBrowser.tracks.find((t) => t.type === TRACK_TYPE.VCF_VARIANT && !t.hidden)) ||
            p.type?.id === PLAYBOOK_TYPE.PROCESSOR),
      );
      setPlaybooks(filteredPlaybooks);
      setLoading(false);
    })();
  }, [configuration.license, dispatch, genomeBrowser?.contig?.organism.id, genomeBrowser.tracks]);

  const getPlaybooksMenuMessage = () => {
    if (isLoading) {
      return "Loading compatible playbooks...";
    } else if (playbooks.length === 0) {
      return "There are no Playbooks that are compatible with your current selection.";
    } else {
      return "Your range selection will be used for analysis.";
    }
  };

  return (
    <MenuListBox
      {...props}
      isLoading={isLoading}
      footNote={
        <div className={menuStyle.foot_note} style={{ minWidth: 380, maxWidth: "unset" }}>
          {getPlaybooksMenuMessage()}
        </div>
      }
      onAction={(key) => {
        const playbook = playbooks.find((p) => String(p.id) === key);
        if (playbook?.type.id === PLAYBOOK_TYPE.ALIGNMENT) {
          const tableQuerySpec = {
            id: genomeBrowser.genome.id,
            idType: DATATABLE_ID_TYPE.ENTITY,
            queryType: ENTITY_TYPE.ORGANISM,
            resultType: ENTITY_TYPE.VCF_VARIANT,
            restrictions: {
              refGenomeId: genomeBrowser.genome.id,
              region: { contigId: genomeBrowser.contig.id, range: genomeBrowser.selectedRange || genomeBrowser.range },
              variantGenomeIds: genomeBrowser.selectedMutantGenomes.map((genome: IMutantGenome) => genome.id),
            },
          } satisfies InputTableQuerySpec;
          dispatch(updateNfPlaybookExecutionState({ reportTableQuerySpec: tableQuerySpec }));
        }
        dispatch(openPlaybook(playbook as Playbook));
      }}
    >
      {playbooks?.map((playbook) => (
        <Item key={String(playbook.id)}>
          <MenuOptionItem
            title={playbook.name}
            description={playbook.description}
            icon={<img width={50} src={getIconElementForPlaybook(playbook).large} alt={"Playbook icon"} />}
            tag={playbook.standard ? <ProtectedResourceLogo size={16} /> : undefined}
          />
        </Item>
      ))}
    </MenuListBox>
  );
};

export default RangeAnalysisPlaybooksButton;
